import { useState, Suspense } from 'react';
import { Box, Grid } from '@mui/material';
import { useL10n } from '@uplift/hooks';
import { ProviderMedicalRole } from '@uplift/types';
import { Loader, Alert } from '@uplift/ui-v2';
import { AppointmentCard, PsychiatristCard, PsychiatryMatching } from '../..';
import { PsychAppointmentBookingSource } from '@uplift/api';
import { useMatchAppointmentStatsQuery, usePsychiatryMatching } from '../../../hooks';
import { VideoCallDrawerContentContainer, VideoCallDrawerContentContainerProps } from '@uplift/video-call';
import { useUserInsurancesQuery, useUserMatchesQuery } from '@uplift/queries';


export type InlinePsychiatryMatchingProps = {
  patientName: string;
  patientId: string;
  onDone: (complete: boolean) => void;
  patientState: string;
};


export const InlinePsychiatryMatching: React.VFC<InlinePsychiatryMatchingProps> = (props) => {
  const l10n = useL10n();
  const { data: matches, refetch } = useUserMatchesQuery(props.patientId, [ 'active' ], { suspense: true });
  const [ showConfirmation, setShowConfirmation ] = useState(false);
  const { data: userInsurancesData } = useUserInsurancesQuery(props.patientId, { suspense: true });
  const userInsurance = userInsurancesData?.[0]?.insurance?.companyId;
  const { psychiatryData, matchingProvidersType } = usePsychiatryMatching({
    stateId: props.patientState,
    insuranceCompanyUuid: userInsurancesData?.[0]?.insurance?.companyId,
    patientId: props.patientId
  }, { suspense: true });

  const handleCloseMatching = async (complete: boolean) => {
    if (complete) {
      await refetch();
      setShowConfirmation(true);
    } else {
      props.onDone(false);
    }
  };

  const psychiatryMatches = matches.activeMatches?.filter(x => x.provider.medicalRole === ProviderMedicalRole.psychiatrist) || [];
  const psychiatryMatch = psychiatryMatches?.[0];
  const { data: matchStats } = useMatchAppointmentStatsQuery(psychiatryMatch?.match.id, { suspense: true });

  const hasAppointments = psychiatryMatches?.length > 0;
  const hasPrevVisit = Boolean(matchStats?.lastAppointment?.fromDatetime);
  const hasNextVisit = Boolean(matchStats?.nextAppointment?.fromDatetime);
  let isPsychiatryAvailable = hasAppointments;

  if (!(hasPrevVisit || hasNextVisit)) {
    isPsychiatryAvailable = false;
  } else if (!userInsurance) {
    isPsychiatryAvailable = false;
  }

  if (hasAppointments) {
    return (
      <Box p={3}>
        <Grid container spacing={4}>
          {showConfirmation && (
            <Grid size={12}>
              <Alert variant="filled" severity="success" onClose={() => setShowConfirmation(false)}>
                <b>{l10n.psychiatry.confirm.scheduled}</b><br />
                {l10n.psychiatry.confirm.sentEmail(props.patientName)}
              </Alert>
            </Grid>
          )}
          <Grid size={12}>
            <PsychiatristCard providerId={psychiatryMatch.provider.id} />
          </Grid>
          {( matchStats.nextAppointment || matchStats.lastAppointment ) && (
            <Grid size={12}>
              <AppointmentCard
                nextAppointment={matchStats.nextAppointment}
                lastAppointment={matchStats.lastAppointment}
              />
            </Grid>
          )}
        </Grid>
      </Box>
    );
  } else if (!isPsychiatryAvailable) {
    return (
      <PsychiatryMatching
        stateId={props.patientState}
        isDrawer
        patientName={props.patientName}
        patientId={props.patientId}
        providers={psychiatryData}
        matchingProvidersType={matchingProvidersType}
        onDone={handleCloseMatching}
        source={PsychAppointmentBookingSource.providerInSession}
      />
    );
  }

  return null;
};


export type PsychiatryReferDrawerProps =
    & Pick<VideoCallDrawerContentContainerProps, 'onClose'>
    & Omit<InlinePsychiatryMatchingProps, 'onDone'>;

export const PsychiatryReferDrawer: React.VFC<PsychiatryReferDrawerProps> = ({ onClose, ...props }) => {

  const onDoneHandler = (_completed: boolean) => {
    onClose();
  };

  return (
    <VideoCallDrawerContentContainer onClose={onClose} title="Refer">
      <Suspense fallback={<Loader color="primary" height="300px" />}>
        <InlinePsychiatryMatching
          {...props}
          onDone={onDoneHandler}
        />
      </Suspense>
    </VideoCallDrawerContentContainer>
  );
};
